import React, { useEffect, useState } from 'react';
import {
  Paper, Stack, Typography, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip, Alert, CircularProgress, Chip, Box, useMediaQuery, useTheme,
} from '@mui/material';
import { Eye, EyeOff, Copy, Check } from 'lucide-react';
import { getAccreditationAccessCodes } from '../../api/api';
import { getApiErrorMessage } from '../../utils/apiError';

const TYPE_LABELS = { banque: 'Banque', point_de_vente: 'Point de vente', controle: 'Contrôle' };
const TYPE_ORDER = Object.keys(TYPE_LABELS);

// Regroupe les accès par catégorie (type), dans l'ordre Banque → Point de vente → Contrôle ; les
// types inconnus passent après, dans leur ordre d'arrivée.
const groupByType = (items) => {
  const byType = new Map();
  for (const c of items) {
    if (!byType.has(c.type)) byType.set(c.type, []);
    byType.get(c.type).push(c);
  }
  const rank = (type) => (TYPE_ORDER.includes(type) ? TYPE_ORDER.indexOf(type) : TYPE_ORDER.length);
  return [...byType.entries()]
    .sort((a, b) => rank(a[0]) - rank(b[0]))
    .map(([type, list]) => ({ type, label: TYPE_LABELS[type] || type || 'Autre', items: list }));
};

// Valeur copiable, masquée par défaut quand c'est un secret (mot de passe, clé d'accès).
const SecretCell = ({ value, secret }) => {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!value) return <Typography sx={{ color: '#9aa4ad' }}>—</Typography>;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.warn('Copie impossible:', err);
    }
  };

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography sx={{ fontFamily: 'monospace', fontSize: 13, wordBreak: 'break-all' }}>
        {secret && !visible ? '••••••••' : value}
      </Typography>
      {secret && (
        <Tooltip title={visible ? 'Masquer' : 'Afficher'}>
          <IconButton size="small" onClick={() => setVisible((v) => !v)} aria-label={visible ? 'Masquer' : 'Afficher'}>
            {visible ? <EyeOff size={14} /> : <Eye size={14} />}
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={copied ? 'Copié' : 'Copier'}>
        <IconButton size="small" onClick={copy} aria-label="Copier">
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </IconButton>
      </Tooltip>
    </Stack>
  );
};

// Une valeur sous son libellé (vue téléphone : une carte par accès à la place du tableau).
const LabeledValue = ({ label, children }) => (
  <Box>
    <Typography sx={{ color: '#5e646a', fontSize: 12 }}>{label}</Typography>
    {children}
  </Box>
);

// Intitulé d'une catégorie d'accès (Banque, Point de vente, Contrôle) avec son effectif.
const GroupHeader = ({ label, count }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ px: { xs: 2, sm: 2 }, py: 0.75, bgcolor: '#f1f5f8' }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#5e646a' }}>{label}</Typography>
    <Chip size="small" label={count} sx={{ height: 18, fontSize: 11, fontWeight: 700 }} />
  </Stack>
);

/**
 * Onglet « Infos accès » de l'espace externe : identifiants / mots de passe (et clé d'accès pour
 * le contrôle) de l'event relié au projet par Wilout. Chargé à l'ouverture de l'onglet seulement.
 */
const AccreditationAccessCodesPanel = ({ configToken }) => {
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const isPhone = useMediaQuery(useTheme().breakpoints.down('sm'));

  useEffect(() => {
    let cancelled = false;
    getAccreditationAccessCodes(configToken)
      .then((data) => { if (!cancelled) setState({ loading: false, data, error: '' }); })
      .catch((err) => {
        if (!cancelled) setState({ loading: false, data: null, error: getApiErrorMessage(err, "Impossible de charger les infos d'accès") });
      });
    return () => { cancelled = true; };
  }, [configToken]);

  if (state.loading) return <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={24} /></Stack>;
  if (state.error) return <Alert severity="info">{state.error}</Alert>;

  const { eventName, items } = state.data;
  if (items.length === 0) {
    return <Alert severity="info">Aucun code d'accès renseigné pour {eventName || 'cet événement'} pour le moment.</Alert>;
  }

  const groups = groupByType(items);

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: 'hidden' }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.25, sm: 1 }}
        sx={{ px: { xs: 2, sm: 2.5 }, py: 1.5, borderBottom: '1px solid #eef1f4' }}
      >
        <Typography sx={{ fontWeight: 700 }}>{eventName}</Typography>
        <Typography sx={{ color: '#5e646a', fontSize: 13 }}>Infos accès — ne les partagez qu'aux personnes concernées.</Typography>
      </Stack>
      {isPhone ? (
        <Stack>
          {groups.map((g) => (
            <React.Fragment key={g.type}>
              <GroupHeader label={g.label} count={g.items.length} />
              <Stack divider={<Box sx={{ borderTop: '1px solid #eef1f4' }} />}>
                {g.items.map((c) => (
                  <Stack key={c.id} spacing={1.25} sx={{ px: 2, py: 1.5 }}>
                    <Typography sx={{ fontWeight: 600, wordBreak: 'break-word' }}>{c.libelle || '—'}</Typography>
                    {c.identifiant && <LabeledValue label="Identifiant"><SecretCell value={c.identifiant} /></LabeledValue>}
                    {c.mot_de_passe && <LabeledValue label="Mot de passe"><SecretCell value={c.mot_de_passe} secret /></LabeledValue>}
                    {(c.cle_acces || c.code) && <LabeledValue label="Clé d'accès"><SecretCell value={c.cle_acces || c.code} secret /></LabeledValue>}
                  </Stack>
                ))}
              </Stack>
            </React.Fragment>
          ))}
        </Stack>
      ) : (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Accès</TableCell>
              <TableCell>Identifiant</TableCell>
              <TableCell>Mot de passe</TableCell>
              <TableCell>Clé d'accès</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groups.map((g) => (
              <React.Fragment key={g.type}>
                <TableRow>
                  <TableCell colSpan={4} sx={{ p: 0 }}><GroupHeader label={g.label} count={g.items.length} /></TableCell>
                </TableRow>
                {g.items.map((c) => (
                  <TableRow key={c.id}>
                    <TableCell>{c.libelle || '—'}</TableCell>
                    <TableCell><SecretCell value={c.identifiant} /></TableCell>
                    <TableCell><SecretCell value={c.mot_de_passe} secret /></TableCell>
                    <TableCell><SecretCell value={c.cle_acces || c.code} secret /></TableCell>
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </Paper>
  );
};

export default AccreditationAccessCodesPanel;
